import { timeSpan } from '../calculations';
import { TimeRange } from '@ardoq/graph';
import {
  TimelineGridPrecisionOptionValue,
  TimelinesGridPrecisionUnit,
} from '@ardoq/api-types';
import { TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE } from '../consts';

type GetScaleForDistanceBetweenGridlinesArgs = {
  targetDistanceBetweenGridllines: number;
  chartWidth: number;
  selectedGridUnitTimeSpan: number;
  availableDomain: [Date, Date];
};
/**
 * Given that if the available domain is equal to the visible domain, the window scale is 1:
 * 1) determine how many units with a given pixel distance would fit into the phases column width;
 * 2) convert this number to a time span;
 * 3) divide this time span by the total available domain span and get the target window scale
 *
 * The target scale should be not larger than 1, to avoid zooming out beyond available domain.
 */
const getScaleForDistanceBetweenGridlines = ({
  targetDistanceBetweenGridllines,
  chartWidth,
  selectedGridUnitTimeSpan,
  availableDomain,
}: GetScaleForDistanceBetweenGridlinesArgs) =>
  Math.min(
    ((chartWidth / targetDistanceBetweenGridllines) *
      selectedGridUnitTimeSpan) /
      timeSpan(availableDomain),
    1
  );

type GetWindowScaleAndZoomLimitsForGridArgs = {
  selectedGridUnitTimeSpan: number;
  minGridLineDistance: number;
  maxGridLinedistance: number;
  chartWidth: number;
  availableDomain: [Date, Date];
  isMultiUnitGrid?: boolean;
};
/**
 * Returns min and max scale for the selected Grid Precision
 */
export const getWindowScaleAndZoomLimitsForGrid = ({
  selectedGridUnitTimeSpan,
  minGridLineDistance,
  maxGridLinedistance,
  chartWidth,
  availableDomain,
  isMultiUnitGrid,
}: GetWindowScaleAndZoomLimitsForGridArgs) => {
  return {
    max: isMultiUnitGrid
      ? 1
      : getScaleForDistanceBetweenGridlines({
          availableDomain: availableDomain,
          chartWidth: chartWidth,
          selectedGridUnitTimeSpan,
          targetDistanceBetweenGridllines: minGridLineDistance,
        }),
    min: getScaleForDistanceBetweenGridlines({
      availableDomain: availableDomain,
      chartWidth: chartWidth,
      selectedGridUnitTimeSpan,
      targetDistanceBetweenGridllines: maxGridLinedistance,
    }),
  };
};

type IsGridPrecisionOptionDisabledArgs = {
  chartDomainRange: TimeRange;
  gridPrecisionViewSettingsValue: TimelinesGridPrecisionUnit;
  smallestGrid: TimelinesGridPrecisionUnit;
  minimumRequiredGridDomain: number;
};
/**
 * Option is disabled if the domain is smaller than the minimum enforced domain for this grid precision
 */
export const isGridPrecisionOptionDisabled = ({
  chartDomainRange: [domainStart, domainEnd],
  gridPrecisionViewSettingsValue,
  smallestGrid,
  minimumRequiredGridDomain,
}: IsGridPrecisionOptionDisabledArgs) => {
  if (gridPrecisionViewSettingsValue === smallestGrid) {
    return false;
  }
  // negative timeSpan should not happen in current design, though it is possible
  return (
    Math.abs(timeSpan([domainStart, domainEnd])) < minimumRequiredGridDomain
  );
};

export const isManualGridOption = (
  optionValue: TimelineGridPrecisionOptionValue
): optionValue is TimelinesGridPrecisionUnit =>
  optionValue !== TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE;
